import React from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ToggleSwitch from './ToggleSwitch';

const COMMODITY_EMOJI = {
  apple: '🍎',
  apples: '🍎',
  apricot: '🍑',
  apricots: '🍑',
  avocado: '🥑',
  avocados: '🥑',
  banana: '🍌',
  bananas: '🍌',
  berry: '🫐',
  blackberry: '🫐',
  blackberries: '🫐',
  blueberry: '🫐',
  blueberries: '🫐',
  cherry: '🍒',
  cherries: '🍒',
  grape: '🍇',
  grapes: '🍇',
  kiwi: '🥝',
  lemon: '🍋',
  lemons: '🍋',
  lime: '🍋',
  limes: '🍋',
  mango: '🥭',
  mangoes: '🥭',
  mangos: '🥭',
  melon: '🍈',
  orange: '🍊',
  oranges: '🍊',
  peach: '🍑',
  peaches: '🍑',
  pear: '🍐',
  pears: '🍐',
  pineapple: '🍍',
  pineapples: '🍍',
  plum: '🍇',
  plums: '🍇',
  raspberry: '🍓',
  raspberries: '🍓',
  strawberry: '🍓',
  strawberries: '🍓',
  tomato: '🍅',
  tomatoes: '🍅',
  watermelon: '🍉',
  watermelons: '🍉',
};

const formatTitleCase = (value) => {
  if (!value) return '';
  return String(value)
    .split(/[_\s-]+/)
    .map((word) => {
      if (word.toLowerCase() === 'fob') return 'FOB';
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

const getCommodityEmoji = (commodity) => {
  const key = String(commodity || '').toLowerCase().trim();
  if (COMMODITY_EMOJI[key]) return COMMODITY_EMOJI[key];
  const singular = key.endsWith('ies')
    ? `${key.slice(0, -3)}y`
    : key.endsWith('s')
      ? key.slice(0, -1)
      : key;
  return COMMODITY_EMOJI[singular] || '🌿';
};

const formatAlertTitle = (alert) => {
  const raw = alert.specific_alert_type || alert.alert_type || '';
  const normalized = String(raw).toLowerCase().replace(/_/g, ' ');
  if (normalized === 'volume' || normalized.includes('volume')) return 'Volume change';
  if (normalized === 'price' || normalized.includes('price')) return 'Price change';
  if (normalized === 'weather' || normalized === 'rain' || normalized === 'temperature') {
    return formatTitleCase(raw) || 'Weather';
  }
  if (normalized === 'market') return 'Market';
  return formatTitleCase(raw) || 'Alert';
};

const firstLabel = (value) => {
  if (Array.isArray(value)) {
    const items = value.map((v) => String(v).trim()).filter(Boolean);
    if (items.length === 0) return null;
    if (items.length === 1) return formatTitleCase(items[0]);
    return 'Multiple';
  }
  if (value == null || value === '') return null;
  return formatTitleCase(value);
};

const getAlertDetailParts = (alert, t) => {
  const skus = alert.skus || {};
  const typesLabel =
    firstLabel(skus.types) ||
    firstLabel(skus.type) ||
    firstLabel(skus.varieties) ||
    firstLabel(skus.variety) ||
    firstLabel(alert.variety) ||
    t('alerts.hub.allTypes');

  const regionCount = Number(alert.number_of_regions) || 0;
  const originsLabel =
    firstLabel(skus.origins) ||
    firstLabel(alert.origin) ||
    (regionCount > 0 ? t('alerts.hub.allOrigins') : t('alerts.hub.allOrigins'));

  const granularity = Array.isArray(alert.granularity)
    ? alert.granularity[0]
    : alert.granularity || alert.frequency || alert.time_period || 'weekly';
  const frequencyLabel = formatTitleCase(granularity) || t('alerts.hub.weekly');

  return [typesLabel, originsLabel, frequencyLabel];
};

const EnabledAlertRow = ({
  alert,
  onToggle,
  onDelete,
  onEdit,
  isToggling,
  isDeleting,
  showDivider,
}) => {
  const { t } = useTranslation();
  const isSmart = (alert._sourceType || '').toLowerCase() === 'smart';
  const title = formatAlertTitle(alert);
  const details = getAlertDetailParts(alert, t).join(' · ');

  return (
    <div
      className={`flex items-center gap-3 py-3 ${
        showDivider ? 'border-t border-[#E8EBE9] dark:border-[#5d625f]' : ''
      } ${isDeleting ? 'opacity-50' : ''}`}
    >
      <span
        className={`inline-flex shrink-0 items-center rounded-[4px] px-1.5 py-0.5 font-['Inter'] text-[10px] font-semibold uppercase tracking-wide ${
          isSmart ? 'bg-[#E3F5EB] text-[#12823F]' : 'bg-[#E8F0FE] text-[#2563EB]'
        }`}
      >
        {isSmart ? 'Smart' : 'Custom'}
      </span>

      <div className="min-w-0 flex-1">
        <p className="font-['Inter'] text-[14px] font-medium leading-snug text-[#282A29] dark:text-white">
          {title}
        </p>
        <p className="mt-0.5 font-['Inter'] text-[12px] leading-snug text-[#828382] dark:text-[#C3C4C3] truncate">
          {details}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <ToggleSwitch
          enabled={Boolean(alert.enabled)}
          onToggle={(e) => {
            e?.stopPropagation?.();
            if (!isToggling) onToggle(alert);
          }}
          disabled={isToggling}
        />
        <button
          type="button"
          onClick={() => onEdit?.(alert)}
          className="flex h-8 w-8 items-center justify-center rounded-[6px] hover:bg-[#F0F5F2] dark:hover:bg-[#282a29] transition-colors"
          aria-label="Edit alert"
        >
          <img
            src="/assets/icons/alerts/edit.png"
            alt=""
            width={18}
            height={18}
            className="h-[18px] w-[18px] object-contain dark:invert"
            aria-hidden
          />
        </button>
        <button
          type="button"
          onClick={() => {
            if (!isDeleting) onDelete(alert);
          }}
          disabled={isDeleting}
          className={`flex h-8 w-8 items-center justify-center rounded-[6px] hover:bg-[#F0F5F2] dark:hover:bg-[#282a29] transition-colors ${
            isDeleting ? 'cursor-not-allowed' : ''
          }`}
          aria-label="Delete alert"
        >
          <img
            src="/assets/icons/alerts/trash.png"
            alt=""
            width={18}
            height={18}
            className="h-[18px] w-[18px] object-contain dark:invert"
            aria-hidden
          />
        </button>
      </div>
    </div>
  );
};

const EnabledCommodityCard = ({
  commodity,
  alerts,
  collapsed,
  onToggleCollapse,
  onToggleAlert,
  onDeleteAlert,
  onEditAlert,
  isAlertToggling,
  isAlertDeleting,
}) => {
  const { t } = useTranslation();
  const count = alerts.length;
  const countLabel =
    count === 1
      ? t('alerts.hub.alertCountOne')
      : t('alerts.hub.alertCountMany', { count });

  return (
    <div className="w-full overflow-hidden rounded-[12px] border border-[#D5D8D6] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d]">
      <button
        type="button"
        onClick={onToggleCollapse}
        className="flex w-full items-center gap-2.5 px-4 py-3.5 text-left"
      >
        <span className="text-[18px] leading-none" aria-hidden>
          {getCommodityEmoji(commodity)}
        </span>
        <span className="font-['Inter'] text-[15px] font-semibold text-[#282A29] dark:text-white">
          {commodity}
        </span>
        <span className="ml-auto inline-flex items-center rounded-full bg-[#F0F5F2] dark:bg-[#282a29] px-2.5 py-1 font-['Inter'] text-[12px] font-medium text-[#5D625F] dark:text-[#C3C4C3]">
          {countLabel}
        </span>
        <ChevronDown
          size={18}
          className={`shrink-0 text-[#282A29] dark:text-white transition-transform duration-300 ease-out ${
            collapsed ? '' : 'rotate-180'
          }`}
        />
      </button>

      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          collapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div
            className={`border-t border-[#E8EBE9] dark:border-[#5d625f] px-4 pb-1 transition-opacity duration-300 ease-out ${
              collapsed ? 'opacity-0' : 'opacity-100'
            }`}
          >
            {alerts.map((alert, index) => (
              <EnabledAlertRow
                key={`${alert._sourceType}-${alert.alert_id || alert.id || index}-${alert.created_at || ''}`}
                alert={alert}
                onToggle={onToggleAlert}
                onDelete={onDeleteAlert}
                onEdit={onEditAlert}
                isToggling={isAlertToggling(alert)}
                isDeleting={isAlertDeleting(alert)}
                showDivider={index > 0}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnabledCommodityCard;
